import Box from '@mui/material/Box';
import React, { useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useDispatch } from 'react-redux';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookMessengerIcon,
  FacebookMessengerShareButton,
  FacebookShareButton,
  PinterestIcon,
  PinterestShareButton,
  TelegramIcon,
  TelegramShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';

import { FirebaseShareEvent } from '../../analytics/firebase';
import Button from '../../components/BasicUI/Button/Button';
import ResponsiveImage from '../../components/Image/ResponsiveImage/ResponsiveImage';
import Modal from '../../components/Modal/Modal';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { FormattedMessage } from '../../util/reactIntl';
import {
  LISTING_PAGE_ROOT_FOR_SHARE_IMAGE,
  LISTING_PAGE_SHARE_LINK_COPY_BUTTON,
  ListingPageGlobalStyles,
} from './listingPageStyles';

// former ListingPage.module.css .shareListingWrapper
const shareListingWrapperSx = {
  display: 'flex',
  alignItems: 'center',
};

// former ListingPage.module.css .shareListingTitle
const shareListingTitleSx = {
  marginLeft: '20px',
};

// former ListingPage.module.css .shareSocialMediaWrapper
const shareSocialMediaWrapperSx = {
  display: 'flex',
  flexDirection: 'column',
};

// former ListingPage.module.css .shareButtonWrapper
const shareButtonWrapperSx = {
  display: 'grid',
  gridTemplateColumns: 'auto auto',
  rowGap: '20px',
};

// former ListingPage.module.css .shareIconWrapper
const shareIconWrapperSx = {
  textAlign: 'left',
  '& span': {
    marginLeft: '10px',
  },
};

// former ListingPage.module.css .shareLink
const shareLinkSx = {
  flex: 3,
};

// former ListingPage.module.css .shareLinkInput (@apply --marketplaceSmallFontStyles; no md block)
const shareLinkInputSx = (theme) => ({
  ...theme.mixins.marketplaceSmallFontStyles,
});

// former ListingPage.module.css .shareLinkDisplayWrapper (@apply --marketplaceSmallFontStyles; no md block)
const shareLinkDisplayWrapperSx = (theme) => ({
  ...theme.mixins.marketplaceSmallFontStyles,
  display: 'flex',
  flexDirection: 'row',
  gap: '20px',
  alignItems: 'center',
  padding: '10px',
  borderRadius: theme.tokens.borderRadius,
  border: `1px solid ${theme.tokens.matterColorNegative}`,
});

const ListingImage = (props) => <ResponsiveImage {...props} />;

const SectionShareModal = ({ open, currentListing, onShareListing }) => {
  const [copied, setCopied] = useState(false);

  const dispatch = useDispatch();
  const onManageDisableScrolling = (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling));

  const title = currentListing && currentListing.attributes.title;
  const description = currentListing && currentListing.attributes.description;
  const firstImage = currentListing.images[0];

  const thisListingUrl =
    typeof document !== 'undefined' && document.querySelector('link[rel=\'canonical\']')
      ? document.querySelector('link[rel=\'canonical\']').getAttribute('href')
      : '';

  const renderSizes = [
    '(max-width: 767px) 75px',
    `(max-width: 1023px) 75px`,
    `(max-width: 1920px) 75px`,
  ].join(', ');

  const shareIconSize = 40;

  const logShareEvent = () => {
    FirebaseShareEvent.withContentType('listing')
      .withItemId(currentListing.id.uuid)
      .withMethod('modalShare')
      .logEvent();
  };

  return (
    <Modal
      id="ShareListing"
      isOpen={open}
      onClose={onShareListing}
      onManageDisableScrolling={onManageDisableScrolling}
    >
      <ListingPageGlobalStyles />
      <FormattedMessage id="ListingPage.shareModal.title" tagName="h2" />

      <Box sx={shareListingWrapperSx}>
        <ListingImage
          alt={title}
          image={firstImage}
          rootClassName={LISTING_PAGE_ROOT_FOR_SHARE_IMAGE}
          sizes={renderSizes}
          variants={['square-small']}
        />
        <Box sx={shareListingTitleSx}>{title}</Box>
      </Box>
      <Box sx={shareSocialMediaWrapperSx}>
        <FormattedMessage id="ListingPage.shareModal.socialMedia" tagName="h3" />
        <Box sx={shareButtonWrapperSx}>
          <FacebookShareButton hashtag="VanSite" onClick={logShareEvent} url={thisListingUrl}>
            <Box sx={shareIconWrapperSx}>
              <FacebookIcon round={true} size={shareIconSize} />
              <FormattedMessage id="ListingPage.shareModal.socialMedia.facebook" />
            </Box>
          </FacebookShareButton>
          <PinterestShareButton
            description={description}
            media={firstImage?.attributes?.variants['landscape-crop2x']?.url}
            onClick={logShareEvent}
            url={thisListingUrl}
          >
            <Box sx={shareIconWrapperSx}>
              <PinterestIcon round={true} size={shareIconSize} />
              <FormattedMessage id="ListingPage.shareModal.socialMedia.pinterest" />
            </Box>
          </PinterestShareButton>
          <TwitterShareButton
            hashtags={['VanSite']}
            onClick={logShareEvent}
            title={title}
            url={thisListingUrl}
          >
            <Box sx={shareIconWrapperSx}>
              <TwitterIcon round={true} size={shareIconSize} />
              <FormattedMessage id="ListingPage.shareModal.socialMedia.twitter" />
            </Box>
          </TwitterShareButton>
        </Box>
      </Box>
      <Box sx={shareSocialMediaWrapperSx}>
        <FormattedMessage id="ListingPage.shareModal.messageAndMail" tagName="h3" />
        <Box sx={shareButtonWrapperSx}>
          <FacebookMessengerShareButton onClick={logShareEvent} url={thisListingUrl}>
            <Box sx={shareIconWrapperSx}>
              <FacebookMessengerIcon round={true} size={shareIconSize} />
              <FormattedMessage id="ListingPage.shareModal.messenger.facebook" />
            </Box>
          </FacebookMessengerShareButton>
          <TelegramShareButton onClick={logShareEvent} title={title} url={thisListingUrl}>
            <Box sx={shareIconWrapperSx}>
              <TelegramIcon round={true} size={shareIconSize} />
              <FormattedMessage id="ListingPage.shareModal.messenger.telegram" />
            </Box>
          </TelegramShareButton>
          <WhatsappShareButton onClick={logShareEvent} title={title} url={thisListingUrl}>
            <Box sx={shareIconWrapperSx}>
              <WhatsappIcon round={true} size={shareIconSize} />
              <FormattedMessage id="ListingPage.shareModal.messenger.whatsapp" />
            </Box>
          </WhatsappShareButton>
          <EmailShareButton
            body={description}
            onClick={logShareEvent}
            subject={title}
            url={thisListingUrl}
          >
            <Box sx={shareIconWrapperSx}>
              <EmailIcon round={true} size={shareIconSize} />
              <FormattedMessage id="ListingPage.shareModal.messenger.email" />
            </Box>
          </EmailShareButton>
        </Box>
      </Box>
      <Box>
        <FormattedMessage id="ListingPage.shareModal.copyLink" tagName="h3" />
        <Box sx={shareLinkDisplayWrapperSx}>
          <Box sx={shareLinkSx}>
            <Box
              component="input"
              readOnly
              sx={shareLinkInputSx}
              type="text"
              value={thisListingUrl}
            />
          </Box>
          <CopyToClipboard
            onCopy={() => {
              setCopied(true);
              logShareEvent();
            }}
            text={thisListingUrl}
          >
            <Button className={LISTING_PAGE_SHARE_LINK_COPY_BUTTON} ready={copied} size="small">
              <FormattedMessage id="ListingPage.shareModal.copy" />
            </Button>
          </CopyToClipboard>
        </Box>
      </Box>
    </Modal>
  );
};

export default SectionShareModal;
